import { createDateObject } from '@front-activerse/common-components';
import type {
  ActiveProductsData,
  ActiveSubscription,
  ActiveSubscriptionOperator,
  ProductAccess,
} from '@front-activerse/common-components';
import type { AccessDescriptionVariant, ActiveProductItem, CancellationInfoKey } from './types';

const OPERATOR_BADGE_KEYS: Partial<Record<ActiveSubscriptionOperator, string>> = {
  AppStore: 'accessCard.label.appStorePayment',
  GooglePlay: 'accessCard.label.googlePlayPayment',
  Stripe: 'accessCard.label.stripePayment',
  Blik: 'accessCard.label.blikRecurringPayment',
};

const OPERATOR_CANCELLATION_INFO_KEYS: Partial<
  Record<ActiveSubscriptionOperator, CancellationInfoKey>
> = {
  AppStore: 'appStore',
  GooglePlay: 'googlePlay',
  Blik: 'blikRecurringPayment',
};

export const DESCRIPTION_KEYS: Record<AccessDescriptionVariant, string> = {
  lifetime: 'accessCard.lifetimeAccessInfo',
  trial: 'accessCard.paymentInfo',
  canceled: 'accessCard.subscriptionEndsInfo',
  nextRenewal: 'accessCard.nextRenewalInfo',
  accessUntil: 'accessCard.accessUntilInfo',
};

const startOfDay = (date: Date): Date =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate());

const toDate = (isoDate: string | null): Date | null => {
  if (!isoDate) return null;
  try {
    return createDateObject(isoDate);
  } catch {
    return null;
  }
};

const isOnOrAfter = (isoDate: string | null, today: Date): boolean => {
  const date = toDate(isoDate);
  return date !== null && startOfDay(date).getTime() >= startOfDay(today).getTime();
};

const isProductAccessValid = (access: ProductAccess, today: Date): boolean =>
  access.lifetimeAccess || isOnOrAfter(access.accessTo, today);

/**
 * Mirrors the Android `ActiveProduct.Subscription.title` resolution so both
 * clients name the same bundle the same way.
 */
export const getSubscriptionTitleKey = (subscription: ActiveSubscription): string => {
  const { givesAccessToDiet, givesAccessToVideoWorkouts, givesAccessToAi } = subscription;

  if (givesAccessToDiet && givesAccessToVideoWorkouts && givesAccessToAi) {
    return 'accessCard.dietAndTrainingsAndAiFeatures.title';
  }
  if (givesAccessToDiet && givesAccessToVideoWorkouts) {
    return 'accessCard.dietAndTrainings.title';
  }
  if (givesAccessToAi) {
    return 'accessCard.aiFeatures.title';
  }
  if (givesAccessToDiet) {
    return 'accessCard.diet.title';
  }
  if (givesAccessToVideoWorkouts) {
    return 'accessCard.trainings.title';
  }
  return '';
};

const getSubscriptionDescriptionVariant = (
  subscription: ActiveSubscription
): AccessDescriptionVariant => {
  if (subscription.isTrial) return 'trial';
  // `canceledAt` is the operator-independent "will not renew" signal; `isCanceled` is kept
  // as a fallback for operators that only flag the subscription as deleted.
  if (subscription.isCanceled || subscription.canceledAt !== null) return 'canceled';
  return 'nextRenewal';
};

const subscriptionHasAccess = (subscription: ActiveSubscription): boolean =>
  subscription.givesAccessToDiet ||
  subscription.givesAccessToVideoWorkouts ||
  subscription.givesAccessToAi ||
  subscription.isTrial;

const mapSubscription = (subscription: ActiveSubscription): ActiveProductItem => {
  const descriptionVariant = getSubscriptionDescriptionVariant(subscription);

  return {
    key: `subscription-${subscription.id}`,
    titleKey: getSubscriptionTitleKey(subscription),
    badgeKey: OPERATOR_BADGE_KEYS[subscription.operator],
    date: toDate(subscription.renewalDate),
    descriptionVariant,
    isTrial: subscription.isTrial,
    subscriptionId: subscription.id,
    canManageSubscription: subscription.operator === 'Stripe',
    // A trial still renews, so it gets the box too; a cancelled one has nothing left to cancel.
    cancellationInfoKey:
      descriptionVariant === 'canceled'
        ? undefined
        : OPERATOR_CANCELLATION_INFO_KEYS[subscription.operator],
  };
};

const mapTimedProduct = (
  key: string,
  titleKey: string,
  accessTo: string | null,
  isLifetime: boolean
): ActiveProductItem => ({
  key,
  titleKey,
  date: isLifetime ? null : toDate(accessTo),
  descriptionVariant: isLifetime ? 'lifetime' : 'accessUntil',
  isTrial: false,
  canManageSubscription: false,
});

/**
 * Builds the "Active products" list: active subscriptions first, then one-off /
 * lifetime product accesses.
 *
 * While a subscription is active, `diet.accessTo` / `videoWorkout.accessTo` /
 * `aiAccess.accessTo` merely echo its renewal date plus grace period, so a timed
 * card for a product the subscription already covers would duplicate the
 * subscription card. Those are skipped; lifetime access is always shown.
 */
export const mapActiveProducts = (
  data: ActiveProductsData,
  today: Date = new Date()
): ActiveProductItem[] => {
  const subscriptions = data.activeSubscriptions.filter(subscriptionHasAccess);

  const coveredByDiet = subscriptions.some((s) => s.givesAccessToDiet);
  const coveredByWorkouts = subscriptions.some((s) => s.givesAccessToVideoWorkouts);
  const coveredByAi = subscriptions.some((s) => s.givesAccessToAi);

  const products: ActiveProductItem[] = subscriptions.map(mapSubscription);

  const { diet, videoWorkout, aiAccess, subscription } = data;

  if (isProductAccessValid(diet, today) && (diet.lifetimeAccess || !coveredByDiet)) {
    products.push(
      mapTimedProduct('diet', 'accessCard.diet.title', diet.accessTo, diet.lifetimeAccess)
    );
  }

  if (
    isProductAccessValid(videoWorkout, today) &&
    subscription.hasWorkoutAccess &&
    (videoWorkout.lifetimeAccess || !coveredByWorkouts)
  ) {
    products.push(
      mapTimedProduct(
        'workouts',
        'accessCard.trainings.title',
        videoWorkout.accessTo,
        videoWorkout.lifetimeAccess
      )
    );
  }

  if (isOnOrAfter(aiAccess.accessTo, today) && !coveredByAi) {
    products.push(mapTimedProduct('ai', 'accessCard.aiFeatures.title', aiAccess.accessTo, false));
  }

  return products;
};
